import styled from '@emotion/styled';
import { css } from '@emotion/react';
import tokens from '@vaad1st/shared.design-tokens/json/variables.json';
import { rgba } from 'polished';
import { BACKGROUND_HEIGHTS, Z_INDICES } from '../../lib/utils/constants';
import { BreakPointToken } from '@vaad1st/hook.media-query';
import { NoGapSupport } from '../../lib/utils/mixins';

type SearchExperienceProps = {
	visible: boolean;
	animate: boolean;
};

type BackgroundPanelProps = {
	show: boolean;
	animate: boolean;
};

type InnerPanelProps = {
	show: boolean;
	animate: boolean;
	initiallyOpen: boolean;
};

type CloseButtonProps = {
	show: boolean;
};

type BookingTypesListProps = {
	show: boolean;
	animate: boolean;
};

type BrandToggleListItemProps = {
	show: boolean;
};

type SearchContainerProps = {
	show: boolean;
	animate: boolean;
};

const {
	default: { opened: defaultOpenedHeight },
	desktop: { opened: desktopOpenedHeight, closed: desktopClosedHeight }
} = BACKGROUND_HEIGHTS;

const getAnimationTime = (animate: boolean): string =>
	animate ? '0.4s' : '0s';
const getAnimationDelay = (animate: boolean): string =>
	animate ? '0.6s' : '0s';
const getAnimationDelayExtended = (animate: boolean): string =>
	animate ? '1s' : '0s';

export const SearchExperience = styled.div<SearchExperienceProps>(
	({ animate, visible }) => {
		return `
			padding: ${tokens.spacing_20px.value}px 0;
			position: fixed;
			top: 0;
			right: 0;
			z-index: ${Z_INDICES.base + 1};
			color: ${tokens.color_light_background_neutral_primary};
			transform ${getAnimationTime(animate)} ease-in-out,
			width ${getAnimationTime(animate)} ease-in-out;
			transform: ${visible ? 'translateX(0)' : 'translateX(100%)'};
			width: ${visible ? '100%' : '0'};
			height: calc(var(--calculated-vh) * 100);
			background: ${tokens.color_light_background_neutral_primary};
			transition: opacity ${getAnimationTime(animate)} ease-in-out, transform ${getAnimationTime(animate)} ease-in-out;

			section[class^='container-module'] {
				position: relative;
				height: 100%;

				@media (orientation: landscape) and (hover: none) and (pointer: coarse) and (max-width: ${BreakPointToken.MdMax}px)  {
					overflow: scroll;
				}
			}

			@media (min-width: ${BreakPointToken.MdMin}px) {
				display: ${visible ? 'block' : 'none'};
				background: none;
				transform: none;
				position: relative;
				height: auto;
				top: auto;
				right: auto;
			}
		`;
	}
);

export const BackgroundPanel = styled('div')<BackgroundPanelProps>(({
	theme,
	show,
	animate
}) => {
	const { palette } = theme;
	const desktopPanelHeight = show ? desktopOpenedHeight : desktopClosedHeight;

	return `
		background-color: var(--color-background-brand-red);
		height: ${defaultOpenedHeight};
		position: absolute;
		top: 0;
		left: 0;
		right: 0;

		@media (min-width: ${BreakPointToken.MdMin}px) {
			transition: opacity ${getAnimationTime(animate)} ease-in-out, height ${getAnimationTime(animate)} ease-in-out;
			height: ${desktopPanelHeight};
			opacity: ${show ? 1 : 0};

			&:after {
				content: '';
				width: 100%;
				height: ${tokens.spacing_4px.value}px;
				background: color-mix(in srgb, var(--color-background-neutral-primary) 16%, transparent);
				position: absolute;
				bottom: ${-1 * tokens.spacing_4px.value}px;
				opacity: ${show ? 1 : 0};
				transition: opacity ${getAnimationTime(animate)} ease-in-out;
			}
		}
	`;
});

export const InnerPanel = styled.div<InnerPanelProps>(
	({ show, animate, initiallyOpen }) => {
		return `
		position: relative;
		height: 100%;

		@media (min-width: ${BreakPointToken.MdMin}px) {
			overflow: visible;
			pointer-events: ${show ? 'auto' : 'none'};
			margin-top: ${show ? '0' : '-5%'};
			margin-bottom: ${show ? '0' : '-5%'};
			opacity: ${show ? '1' : '0'};

			transition:
				margin ${getAnimationTime(animate)} ease-in-out,
				opacity ${getAnimationTime(animate)} ease-in-out,
				max-height ${getAnimationTime(animate)} ease-in-out,
				transform ${getAnimationTime(animate)} ease-in-out;
		}
	`;
	}
);

export const CloseButton = styled.div<CloseButtonProps>(({ show }) => {
	return `
		margin-inline-start: auto;
		opacity: ${show ? `1` : '0'};
		transition:
		opacity 0.4s ease-in-out;
	`;
});

export const Header = styled.div`
	display: flex;
	gap: ${tokens.spacing_16px.value}px;
	align-items: center;
	justify-content: space-between;
`;

export const Heading = styled.div`
	@media (min-width: ${BreakPointToken.MdMin}px) {
		&:not(.toggle-hidden) {
			display: none;
		}
	}
`;

export const BrandToggle = styled.ul<BookingTypesListProps>(
	({ show, animate }) => css`
		list-style: none;
		display: inline-flex;
		background: var(--temp-token-color-transparent-contrast-16);
		padding: ${tokens.spacing_8px.value}px;
		border-radius: 30px;
		width: 100%;
		max-width: 100%;
		transition:
			gap ${getAnimationTime(animate)} ease-in-out ${getAnimationDelay(animate)},
			opacity ${getAnimationTime(animate)} ease-in-out
				${getAnimationDelay(animate)},
			transform ${getAnimationTime(animate)} ease-in-out
				${getAnimationDelay(animate)};

		opacity: ${show ? 1 : 0};
		transform: ${show ? 'translateX(0)' : 'translateX(-5%)'};

		@media (min-width: ${BreakPointToken.SmMin}px) {
			gap: ${tokens.spacing_8px.value}px;
			width: auto;
		}
	`
);

export const BrandToggleListItem = styled.li<BrandToggleListItemProps>(
	({ show }) => css`
		flex: 1 1 auto;

		@media (min-width: ${BreakPointToken.SmMin}px) {
			min-width: 0;
			width: 228px;
			max-width: 100%;
		}

		/** Added for AB Test */
		&[data-test-flight-plus-hotel-variant] {
			display: none;
		}
		/** */

		${NoGapSupport(
			() => css`
				transition: margin ${getAnimationTime(show)} ease-in-out
					${getAnimationDelay(show)};

				& + & {
					margin: ${show ? `0 0 0 ${tokens.spacing_8px.value}px` : 0};

					@media (min-width: ${BreakPointToken.MdMin}px) {
						margin: 0 0 0 ${tokens.spacing_8px.value}px;
					}
				}
			`
		)}

		button {
			background: transparent;
			width: 100%;

			/** Added for AB Test */
			white-space: nowrap;
			font-size: ${tokens.font_body_xs_font_size_screen_xs.value}px !important;

			@media (min-width: ${BreakPointToken.SmMin}px) {
				font-size: initial !important;
			}
			/** */
		}
	`
);

export const SaleFlashOuter = styled.p`
	display: none;
	@media (min-width: ${BreakPointToken.MdMin}px) {
		display: block;
	}
	position: absolute;
	top: -24px;
	left: 50%;
	transform: translateX(-50%);

	&:empty {
		span {
			display: none;
		}
	}

	span {
		background: var(--color-background-semantic-promotional-subtle);
		color: var(--color-text-semantic-promotional);
		display: block;
		border-radius: 4px;
		padding: 4px 8px;
		max-width: 20ch;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;

		&:empty {
			display: none;
		}
	}
`;

export const SearchContainer = styled.section<SearchContainerProps>(
	({ show, animate }) => {
		return `
			background: var(--color-background-neutral-primary);
			color: var(--color-text-neutral-primary);
			border-radius: ${tokens.spacing_12px.value}px ${tokens.spacing_12px.value}px 0 0;
			margin: 0 ${-1 * tokens.spacing_16px.value}px;
			padding: ${tokens.spacing_16px.value}px;
			height: calc((var(--calculated-vh) * 100) - (${defaultOpenedHeight} - ${tokens.spacing_12px.value}px));
			transition:
				opacity ${getAnimationTime(animate)} ease-in-out ${getAnimationDelayExtended(animate)},
				transform ${getAnimationTime(animate)} ease-in-out ${getAnimationDelayExtended(animate)};

			opacity: ${show ? 1 : 0};
			transform: ${show ? 'none' : 'translateY(-2%)'};
			overflow-y: auto;
			overflow-x: hidden;

			@media (min-width: ${BreakPointToken.MdMin}px) {
				overflow: initial;
				border-radius: ${tokens.spacing_12px.value}px;
				height: auto;
				margin: ${tokens.spacing_20px.value}px 0 0;
				padding: ${tokens.spacing_16px.value}px;
				opacity: 1;
				transform: none;
				box-shadow: 0 ${tokens.spacing_4px.value}px ${tokens.spacing_16px.value}px 0 color-mix(in srgb, ${tokens.color_light_text_neutral_primary} 16%, transparent);
				transition:
					opacity ${getAnimationTime(animate)} ease-in-out ${getAnimationDelay(animate)},
					transform ${getAnimationTime(animate)} ease-in-out ${getAnimationDelay(animate)};
			}

			@media (min-width: ${BreakPointToken.LgMin}px) {
				padding: ${tokens.spacing_16px.value}px;
			}

			&.toggle-hidden {
				margin-top: ${tokens.spacing_24px.value}px;
			}
		`;
	}
);
